/**
 * Dynamically selects an appropriate postal code mask based on the current input and appended character(s).
 * This function is designed to be used in scenarios where postal code formats can vary significantly,
 * such as in countries with multiple postal code formats. If no specific mask matches, it defaults to the first
 * mask in the array as a fallback.
 *
 * @param {Array<PostalCodeMask>} `masks` - An array of mask objects, each containing a `pattern` RegEx
 *                                used to match against the complete input value.
 * @returns {Function} A curried function that takes two parameters:
 *   - `appended` {string}: The character(s) being added to the current input value.
 *   - `dynamicMasked` {Object}: An object containing the current input value and the array of compiled masks.
 *                               `dynamicMasked.value` is the current input value, and `dynamicMasked.compiledMasks`
 *                               is an array of compiled mask objects to apply based on the input.
 *
 * @returns {Object} Returns the selected mask based on the input value and appended character(s).
 */
export const selectDynamicPostalCodeMask = (masks) => (appended, dynamicMasked) => {
    // The complete value that would result if appended is accepted
    const completeValue = dynamicMasked.value + appended;
    // Try to find a mask that starts with the pattern of completeValue
    const targetMaskIndex = masks === null || masks === void 0 ? void 0 : masks.findIndex((m) => {
        if (m === null || m === void 0 ? void 0 : m.pattern) {
            return new RegExp(m.pattern).test(completeValue);
        }
    });
    // If a target mask is found, return the appropriate mask
    if (targetMaskIndex !== -1) {
        return dynamicMasked.compiledMasks[targetMaskIndex];
    }
    // If no mask is found, default to the first mask as a fallback
    return dynamicMasked.compiledMasks[0];
};
